import { dateAdd, dateFormat, dateFormatYMD, dateGetDate, dateGetYear, dateIsAfter, dateIsBefore, dateIsToday } from '@utils/dateFns';
import { deepCopy, fadeIn, hashObject, hideElement, isMobileView, l, lLink, lc, logEvent, qs, qsa, showElement, sleep, storedItemCreate, storedItemRead } from '@utils/toolbox';
import { Base64 } from '@js/plugins/base64.js';
import { ConnectApi } from '@js/plugins/connectApi';
import { eventCategories, events } from '@js/utils/event';

import '@/inc/module/PriceCutter/_pricecutter.scss';
import { resultsRender } from '@js/components/resultsRender';
import { getHolidayData } from '@js/holidays/datepicker.holidays';

const _defaults = {
	offset: 2,
	dates: {
		today: js_params.isoDates.today,
		from: js_params.isoDates.from,
		to: js_params.isoDates.to
	},
	dateRange: js_params.dateRange,
	weekdays: js_params.weekdays,
	errors: {
		notEnoughData: l('module.pricecutter.error.notEnoughData'),
		tripTooLong: l('module.pricecutter.error.tripTooLong'),
		departNotSet: l('searchBox.error.notSetDeparture', 'Vänligen fyll i en avreseort'),
		returnNotSet: l('searchBox.error.notSetDestination', 'Vänligen fyll i en destinationsort')
	}
};

let promise;
let requestId;

function getData (parameters) {
	const newRequestId = hashObject(parameters);
	if (requestId === newRequestId) {
		return promise;
	}

	requestId = newRequestId;

	promise = ConnectApi('PriceCutterAPI', 'getEstimatedPrices', Object.values(parameters));
	return promise;
}

export class PriceCutter {
	constructor (params) {
		const { searchData, isMini = false, container } = params;

		this.searchData = searchData;
		this.container = container;

		this.originalSearchData = deepCopy(searchData);

		if (!this.container) {
			return;
		}

		this.isMini = isMini;

		this.initialized = false;
		this.today = new Date(js_params.isoDates.today);
		this.lastSelectableDay = dateAdd(this.today, { days: 364 });

		this.outbounds = null;
		this.inbounds = null;
		this.returnTrip = null;
		this.departDates = [];
		this.returnDates = [];
		this.multiplier = null;
		this.baseline = null;
		this.selectedBound = false;

		this.minPrice = 0;
		this.maxPrice = 100000;

		const classes = {
			mini: {
				depart: 'pricecutter-mini__graph--depart',
				departScroller: 'pricecutter-mini__graph--depart',
				return: 'pricecutter-mini__graph--return',
				isReturn: 'pricecutter-mini--return',
				scroller: 'pricecutter-mini__scroller',
				activeDay: 'pricecutter-mini__day--active',
				day: 'pricecutter-mini__day',
				disabledDay: 'pricecutter-mini__day--disabled',
				holiday: 'pricecutter-mini__day--holiday',
				bar: 'pricecutter-mini__bar',
				date: 'pricecutter-mini__date',
				days: 'pricecutter-mini__days',
				dateDay: 'pricecutter-mini__dateday',
				dateNum: 'pricecutter-mini__datenum',
				medianLine: 'pricecutter-mini__medianLine',
				popup: 'pricecutter-mini__datePricePopup',
				error: 'pricecutter-mini__error',
				hasError: 'pricecutter-mini--hasError',
				estimatedText: 'pricecutter-mini__estTxt',
				estimatedPrice: 'pricecutter-mini__price',
				estimated: 'pricecutter-mini__estimated',
				selectedDates: 'pricecutter-mini__selectedDates'
			},
			full: {
				depart: 'abPriceCutter__graph--depart',
				return: 'abPriceCutter__graph--return',
				isReturn: 'abPriceCutter--return',
				activeDay: 'abPriceCutter__day--active',
				disabledDay: 'abPriceCutter__day--disabled',
				holiday: 'abPriceCutter__day--holiday',
				day: 'abPriceCutter__day',
				days: 'abPriceCutter__days',
				date: 'abPriceCutter__date',
				dateDay: 'abPriceCutter__dateday',
				dateNum: 'abPriceCutter__datenum',
				bar: 'abPriceCutter__bar',
				medianLine: 'abPriceCutter__medianLine',
				popup: 'abPriceCutter__datePricePopup',
				error: 'abPriceCutter__error',
				hasError: 'abPriceCutter--hasError',
				scroller: 'abPriceCutter__scroller',
				estimatedText: 'abPriceCutter__estTxt',
				estimatedPrice: 'abPriceCutter__price',
				estimated: 'abPriceCutter__estimated',
				estimatedVisible: 'abPriceCutter__estimated--visible',
				selectedDates: 'abPriceCutter__selectedDates'
			}
		};

		this.classes = isMini ? classes.mini : classes.full;
		this.selectors = Object.fromEntries(Object.entries(this.classes).map(([k, v]) => [k, '.' + v]));

		this.datePricePopup = this.container.querySelector(this.selectors.popup);

		this.searchParams = this.getSearchParameters();

		this.setHolidays().then(() => {
			this.events();

			if (this.isMini && isMobileView()) {
				this.doRequest();
			}
		});
	}

	load () {
		showElement(this.container);

		if (this.initialized) {
			return;
		}

		this.request?.abort?.();

		let error = false;

		if (this.searchData.fromIata === 'undefined') {
			error = _defaults.errors.departNotSet;
		} else if (this.searchData.toIata === 'undefined') {
			error = _defaults.errors.returnNotSet;
		}

		if (error) {
			this.renderError(error);
			return;
		}

		this.doRequest();
	}

	doRequest () {
		if (this.selectedBound) {
			this.hideEstimatedPrice();
		}

		if (!resultsRender.response?.meta?.lowprice) {
			return this.renderError(_defaults.errors.notEnoughData);
		}
		this.searchData.price = resultsRender.response.meta.lowprice;
		this.request = getData(this.searchData).then((response) => {
			this.parseResponse(response);
			return response;
		});
	}

	parseResponse (response) {
		let error = false;

		if (!response) {
			error = _defaults.errors.notEnoughData;
		}

		if (response.errorCode || !response) {
			if (response.errorCode === 1) {
				error = _defaults.errors.notEnoughData;
			} else if (response.errorCode === 2) {
				error = _defaults.errors.tripTooLong;
			}

			if (error) {
				this.renderError(error);
				return;
			}

			// if (error || this.selectedBound === false)
			// 	return;
		} else {
			this.outbounds = response.outbounds;
			this.inbounds = response.inbounds;
			this.returnTrip = response.returnTrip;
			this.departDates = [];
			this.returnDates = [];
			this.multiplier = response.multiplier;
			this.baseline = response.baseline;

			for (let i = 0; i < this.outbounds.length; i++) {
				this.departDates.push(new Date(this.outbounds[i].departDate));

				if (this.returnTrip) {
					this.returnDates.push(new Date(this.inbounds[i].returnDate));
				}
			}

			this.updatePriceRanges();
			this.render();
			this.showEstimatedPrice();
		}

		this.initialized = true;
	}

	async parseEstimates (response) {
		let error = false;

		if (response.errorCode) {
			if (response.errorCode === 1) {
				error = true;
				alert(_defaults.errors.notEnoughData);
			} else if (response.errorCode === 2) {
				error = true;
				alert(_defaults.errors.tripTooLong);
			}
		} else {
			this.outbounds = response.outbounds;
			this.inbounds = response.inbounds;
			this.returnTrip = response.returnTrip;
			this.departDates = [];
			this.returnDates = [];
			this.multiplier = response.multiplier;
			this.baseline = response.baseline;

			for (let i = 0; i < this.outbounds.length; i++) {
				this.departDates.push(new Date(this.outbounds[i].departDate));

				if (this.returnTrip) {
					this.returnDates.push(new Date(this.inbounds[i].returnDate));
				}
			}

			await sleep(300);

			this.update();

			this.showEstimatedPrice();
		}
	}

	render () {
		let html = this.renderGraph('depart');

		this.container.querySelector(`${this.selectors.depart} ${this.selectors.scroller}`).innerHTML = html;

		if (this.returnTrip) {
			this.container.classList.add(this.classes.isReturn);

			html = this.renderGraph('return');
			this.container.querySelector(`${this.selectors.return} ${this.selectors.scroller}`).innerHTML = html;
		} else {
			this.container.classList.remove(this.classes.isReturn);
		}

		qsa('.abPriceCutter').forEach((element) => {
			showElement(element);
			element.classList.add('fade-in');
		});

		this.animateBars();

		this.scrollToActiveBars();
	}

	renderError (error) {
		this.container.classList.add(this.classes.hasError);
		if (this.isMini) {
			return;
		}
		fadeIn(this.container);
		this.container.querySelector(this.selectors.error).innerHTML = `<span>${error}</span>`;
	}


	update () {
		// 1. Recalculate ranges and heights
		this.updatePriceRanges();

		// 2. Update data attributes and evaluation on each day element
		const updateDays = (bounds, selector, isOutbound) => {
			qsa(`${isOutbound ? '.abPriceCutter__graph--return' : '.abPriceCutter__graph--depart'} .abPriceCutter__bar`).forEach(e => e.classList.add('disabled'));

			bounds.forEach((bound) => {
				const dateKey = dateFormatYMD(new Date(isOutbound ? bound.returnDate : bound.departDate));
				const dayElement = this.container.querySelector(`${selector} ${this.selectors.day}[data-date="${dateKey}"]`);

				if (!dayElement) {
					return;
				}

				// Update raw price
				dayElement.dataset.price = lc(bound.price);
				dayElement.dataset.price = lc(bound.price);

				// Update percent
				const percent = this.getBarHeightPercent(bound.price, !isOutbound);
				const percentForDom = `${percent}%`;
				dayElement.dataset.percent = percentForDom;
				dayElement.setAttribute('data-evaluation', percent > (this.isMini ? 75 : 50) ? 'expensive' : 'cheap');
				// console.log(`${isOutbound} ${this.selectors.day}[data-date="${dateKey}"] --- ${bound.price} ${percentForDom}`);
				qs('.abPriceCutter__bar', dayElement).classList.remove('disabled');
				qs('.abPriceCutter__bar', dayElement).style.height = percentForDom;
			});
		};

		// Depart days
		updateDays(this.outbounds, this.selectors.depart, false);

		// Return days (if present)
		if (this.returnTrip) {
			updateDays(this.inbounds, this.selectors.return, true);
		}
	}

	renderGraph (bound, update = false) {
		let html = ``;
		const bounds = (bound === 'depart') ? this.outbounds : this.inbounds;
		let prevOutMonth = new Date(bounds[0].departDate).getMonth() - 1;

		for (let i = 0; i < bounds.length; i++) {
			const price = bounds[i].price;
			const date = (bound === 'depart') ? new Date(bounds[i].departDate) : new Date(bounds[i].returnDate);
			const currMonth = ((date.getMonth() + 1) > 12) ? 1 : (date.getMonth() + 1);

			if (currMonth !== prevOutMonth) {
				if (i > 0) {
					html += `</div></div>`;
				}


				if (!this.isMini) {
					html += `<div class="abPriceCutter__month">`;
					html += this.renderMonth({
						year: dateGetYear(date),
						month: dateFormat(date, 'MMM')
					});
				}

				html += `<div class="${this.classes.days}">`;
			}

			const isOutbound = (bound === 'depart');

			html += this.renderDay({
				bound: bound,
				date: isOutbound ? bounds[i].departDate : bounds[i].returnDate,
				price: price
			}, update, isOutbound);

			prevOutMonth = currMonth;
		}

		html += `</div></div>`;

		return html;
	}

	renderMonth (obj) {
		return `<div class="abPriceCutter__monthstr"><span class="bg-white">${obj.month} ${obj.year}</span></div>`;
	}

	renderDay (obj, update = false, isOutbound = false) {
		const dateObject = new Date(obj.date);
		const date = dateFormatYMD(dateObject);
		const dateLong = dateFormat(dateObject, 'd MMM yyyy');
		let percent = parseFloat(this.getBarHeightPercent(obj.price, isOutbound).toFixed(2));

		const cheapThreshold = this.isMini ? 75 : 50;

		const evaluation = percent > cheapThreshold ? 'expensive' : 'cheap';
		percent += '%';

		const weekday = dateFormat(dateObject, 'eee');
		const weekdayShort = dateFormat(dateObject, 'eeeeee');
		const dateNum = dateGetDate(dateObject);

		// let styles = this.classes.day;
		const stylesArray = [this.classes.day];

		if (dateIsToday(dateObject) || dateIsBefore(dateObject, this.today) || dateIsAfter(dateObject, this.lastSelectableDay)) {
			// styles += ' ' + this.classes.disabledDay;
			stylesArray.push(this.classes.disabledDay);
		}

		if (obj.bound === 'depart') {
			if (this.searchData.departDate === date) {
				stylesArray.push(this.classes.activeDay);
			}
		} else if (this.searchData.returnDate === date) {
			stylesArray.push(this.classes.activeDay);
		}

		if (this.holidays) {
			const isHoliday = this.holidays.has(date);

			if (isHoliday) {
				stylesArray.push(this.classes.holiday);
			}
		}

		const styles = stylesArray.join(' ');

		return `
				<div class="${styles}"
					aria-label="${dateLong}"
					data-date="${date}"
					data-datelong="${dateLong}"
					data-price="${lc(obj.price)}"
					data-bound="${obj.bound}"
					data-percent="${percent}"
					data-evaluation="${evaluation}">
					<div class="${this.classes.bar}" ${(update) ? `style="height:${percent}"` : ``}></div>
					<div class="${this.classes.date}">
						${this.isMini ? `
							<span class="pricecutter-mini__dateday">${weekdayShort}</span>
							<span class="pricecutter-mini__datenum">${dateNum}</span>
						` : `
							<span class="${this.classes.dateDay}">
								<span class="block md:hidden">${weekday}</span>
								<span class="hidden md:block">${weekdayShort}</span>
							</span>
							<span class="${this.classes.dateNum}">${dateNum}</span>
						`}
					</div>
				</div>
			`;
	}

	async setHolidays () {
		this.holidays = new Set();

		const holidayData = await getHolidayData();
		for (const hol in holidayData) {
			this.holidays.add(hol);
		}
	}

	scrollToActiveBars () {
		qsa(this.selectors.activeDay, this.container).forEach((element) => {
			element.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' });
		});
	}

	getBarHeightPercent (price, isOutbound = false) {
		const object = isOutbound ? this.minMaxPrices.outbounds : this.minMaxPrices.inbounds;
		return parseFloat(((price - object.min) / (object.max - object.min) * 100).toFixed(2));
	}

	animateBars () {
		const days = this.container.querySelectorAll(this.selectors.day);

		days.forEach((element) => {
			element.querySelector(this.selectors.bar).style.height = element.dataset.percent;
		});
	}

	updatePriceRanges () {
		const prices = {
			outbounds: this.outbounds.map(outbound => parseInt(outbound.price)),
			inbounds: this.returnTrip ? this.inbounds.map(inbound => parseInt(inbound.price)) : []
		};


		this.minMaxPrices = {
			outbounds: {
				min: Math.min(...prices.outbounds),
				max: Math.max(...prices.outbounds)
			},
			inbounds: {
				min: Math.min(...prices.inbounds),
				max: Math.max(...prices.inbounds)
			}
		};

		this.minMaxPrices.outbounds.average = prices.outbounds.reduce((a, b) => a + b, 0) / prices.outbounds.length;
		this.minMaxPrices.inbounds.average = prices.inbounds.reduce((a, b) => a + b, 0) / prices.inbounds.length;

		return prices;
	}


	showEstimatedPrice () {
		if (this.isMini) {
			return;
		}
		const price = this.container.querySelector('.abPriceCutter__day--active').dataset.price;
		const text = this.returnTrip ? l('module.pricecutter.estimatedPriceReturn') : l('module.pricecutter.estimatedPriceOneWay');

		this.container.querySelector(this.selectors.estimatedText).innerHTML = text;
		this.container.querySelector(this.selectors.estimatedPrice).innerHTML = price;
		this.container.querySelector(this.selectors.estimated).classList.add(this.classes.estimatedVisible);
	}

	hideEstimatedPrice () {
		this.container.querySelector(this.selectors.estimated).classList.remove(this.classes.estimatedVisible);
	}

	updateFooter () {
		const selectedDates = this.container.querySelectorAll(this.selectors.selectedDates);
		let tabletStr = dateFormat(new Date(this.searchData.departDate), 'PPP');

		if (this.returnTrip) {
			tabletStr += ` - ${dateFormat(new Date(this.searchData.returnDate), 'PPP')}`;
		}

		selectedDates[0].children[0].innerText = tabletStr;

		qsa('button[data-action="search-price-cutter"]').forEach((element) => {
			showElement(element);
		});

		qsa('button[data-action="close-price-cutter"]').forEach((element) => {
			hideElement(element);
		});
	}

	events () {
		if (this.isMini) {
			return;
		}

		const html = this.container;

		let resizeTimer;

		window.addEventListener('resize', (e) => {
			if (!this.initialized) {
				return;
			}

			if (resizeTimer) {
				cancelAnimationFrame(resizeTimer);
			}

			resizeTimer = requestAnimationFrame(() => {
				this.datePricePopup.style.opacity = 0;
			});
		});

		html.addEventListener('click', (e) => {
			const elem = e.target;

			if (elem.closest('.abPriceCutter__day')) {
				const dayBar = elem.closest(this.selectors.day);

				if (dayBar.classList.contains(this.classes.activeDay) || dayBar.classList.contains(this.classes.disabledDay)) {
					// If day is already active
					return;
				} else {
					// If day is not disabled, update data
					const bound = dayBar.dataset.bound;
					const date = dayBar.dataset.date;

					// enforce depart/return ordering
					this.forceValidDates(bound, date);

					// finally, assign the clicked date
					if (bound === 'depart') {
						this.searchData.departDate = date;
					} else {
						this.searchData.returnDate = date;
					}


					this.selectedBound = bound;

					this.selectedBound = bound;

					this.hideEstimatedPrice();
					this.updateEstimates();
					this.updateFooter();
				}

				qsa(this.selectors.activeDay, qs(this.selectors[dayBar.dataset.bound])).forEach((element) => {
					element.classList.remove(this.classes.activeDay);
				});
				dayBar.classList.add(this.classes.activeDay);
			}
		}, false);

		html.addEventListener('mouseover', (e) => {
			const elem = e.target.closest('.abPriceCutter__day');

			if (elem) {
				const { left, top } = qs(this.selectors.bar, elem).getBoundingClientRect();
				const { datelong, price } = elem.dataset;

				this.showDatePricePopup({
					datelong,
					price,
					position: {
						left: left + 'px',
						top: top + 'px'
					}
				});
			}
		});

		html.addEventListener('mouseout', (e) => {
			const elem = e.target;

			if (elem.closest(this.selectors.bar)) {
				this.datePricePopup.style.opacity = 0;
			}
		});

		document.addEventListener('click', (e) => {
			const elem = e.target;

			if (elem.matches('button')) {
				const action = elem.dataset.action;

				if (action === 'search-price-cutter') {
					e.stopPropagation();
					this.search();
					return;
				}

				if (action === 'close-price-cutter') {
					this.close();
				}
			}
		});
	}

	getSearchParameters () {
		let direct = 0;
		let nearbyDates = 0;
		let baggage = 0;
		const userSavedSearch = storedItemRead('userSavedSearch');

		if (userSavedSearch) {
			direct = parseInt(userSavedSearch.direct, 10);
			baggage = parseInt(userSavedSearch.baggage, 10);
			nearbyDates = parseInt(userSavedSearch.nearbyDates, 10);
		}

		const cabin = parseInt(js_params.classType, 10);
		const legs = JSON.parse(JSON.stringify(js_params.showresultSearchParams));

		let passengers = {};
		// Format passenger data
		if ('youthAges' in js_params && js_params.youthAges.length > 0) {
			passengers = {
				youth: {
					count: js_params.passengers,
					ages: js_params?.youthAges || []
				}
			};
		} else {
			passengers = {
				adults: js_params.adults,
				child: {
					count: js_params.children,
					ages: js_params.childrenAges
				}
			};
		}

		const searchParams = {
			tripType: js_params.returntrip,
			direct,
			baggage,
			nearbyDates,
			cabin,
			legs,
			passengers
		};

		return searchParams;
	}

	updateSearchParameters () {
		this.searchParams.legs[0].departDate = this.searchData.departDate;
		this.searchParams.legs[0].returnDate = this.searchData.returnDate;
	}

	showDatePricePopup (opts) {
		this.datePricePopup.innerHTML = `
				<div>
					<span>${opts.datelong}</span>
				</div>
				<div class="price">
					<span>${opts.price}</span>
				</div>
			`;

		this.datePricePopup.style.left = opts.position.left;
		this.datePricePopup.style.top = opts.position.top;
		this.datePricePopup.style.opacity = 1;
	}

	updateEstimates () {
		this.request = getData(this.searchData).then((response) => {
			this.parseEstimates(response);
			return response;
		});
	}

	close () {
		// this.displayDirector.hide();
		// this.displayDirector.toggleViewType('pricecalendar');
	}

	search () {
		this.updateSearchParameters();

		const postData = this.getPostData();
		this.initiateSearch(postData);
	}

	initiateSearch (postData) {
		const handleError = () => {
			this.cancelSearch();
			window.alert(l('error.e9'));
		};


		logEvent(events.cuttersearch, eventCategories.search);

		this.request = ConnectApi('Search', 'initiateSearch', [postData]).then((resp) => {
			const response = resp.results;
			const errors = resp.error;
			const { direct, baggage } = this.searchParams;

			if (errors) {
				errors.forEach(error => window.alert(error));
				return;
			}

			if (typeof response.id !== 'undefined') {
				const filterParams = {};

				if (direct === 1) {
					filterParams.numStops = 0;
				}

				if (baggage === 1) {
					filterParams.baggageIncluded = 1;
				}

				this.saveSearchToLocalStorage(this.searchParams);

				location.href = this.getShowresultUrl(response.id, filterParams);
			}
		}).catch(handleError);
	}

	getPostData () {
		const legs = this.searchParams.legs[0];
		const postData = {
			returntrip: this.searchParams.tripType,
			classType: this.searchParams.cabin,
			passengers: this.searchParams.passengers,
			trips: [
				{
					from: [{
						iata: legs.fromIata,
						isMetro: legs.fromMetro
					}],
					to: [{
						iata: legs.toIata,
						isMetro: legs.toMetro
					}],
					date: legs.departDate
				}
			]
		};

		if ([1, 2].includes(js_params.returntrip)) {
			postData.trips.push({
				from: [{
					iata: legs.toIata,
					isMetro: legs.toMetro
				}],
				to: [{
					iata: legs.fromIata,
					isMetro: legs.fromMetro
				}],
				date: legs.returnDate
			});
		}

		return postData;
	}

	saveSearchToLocalStorage (data = null) {
		if (!data || data === {}) {
			console.log('Tried to save empty user search');
			return;
		}
		storedItemCreate('userSavedSearch', data);
	}

	getShowresultUrl (id, filterParams) {
		let targetUrl = `/${lLink('global.navigation.resultsLink')}?id=${id}`;

		if (Object.keys(filterParams).length) {
			targetUrl += `&filter=${Base64.encode(JSON.stringify(filterParams))}`;
		}

		return targetUrl;
	}

	cancelSearch () {
		this.isSearching = false;
		this.request.abort();
	}

	forceValidDates (bound, date) {
		if (!this.returnTrip) {
			return;
		}

		const selected = new Date(date);
		const departDateObj = new Date(this.searchData.departDate);
		const returnDateObj = new Date(this.searchData.returnDate);

		if (bound === 'depart') {
			// if depart > return, bump return to next day
			if (dateIsAfter(selected, returnDateObj)) {
				const newReturn = dateAdd(selected, { days: 1 });
				const newReturnYMD = dateFormatYMD(newReturn);
				this.updateDateAndUI('return', returnDateObj, newReturnYMD);
			}
		} else {
			// bound === 'return'
			// if return < depart, move depart to previous day
			if (dateIsBefore(selected, departDateObj)) {
				const newDepart = dateAdd(selected, { days: -1 });
				const newDepartYMD = dateFormatYMD(newDepart);
				this.updateDateAndUI('depart', departDateObj, newDepartYMD);
			}
		}
	}

	/**
		 * Internal helper to update searchData and activeDay UI for a bound.
		 * @param {'depart'|'return'} which - which bound to update
		 * @param {Date} oldDateObj - the previous date object
		 * @param {string} newYMD - the new date string in YMD format
		 */
	updateDateAndUI (which, oldDateObj, newYMD) {
		this.searchData[which + 'Date'] = newYMD;
		// remove old active class
		const oldElem = this.container.querySelector(`${this.selectors[which]} ${this.selectors.day}[data-date="${dateFormatYMD(oldDateObj)}"]`);
		if (oldElem) {
			oldElem.classList.remove(this.classes.activeDay);
		}
		// add new active class
		const newElem = this.container.querySelector(`${this.selectors[which]} ${this.selectors.day}[data-date="${newYMD}"]`);
		if (newElem) {
			newElem.classList.add(this.classes.activeDay);
		}
	}

	/**
	 * Returns the two cheapest outbound and inbound dates
	 * @returns
	 */
	async getCheapestDates () {
		// Check if inbounds has been set, if not call doRequest
		if (!this.inbounds) {
			this.doRequest();
			await this.request;
		}

		// Check if outbounds has been set, if not call doRequest
		if (!this.inbounds) {
			return [];
		}


		// check if return trip is enabled
		if (this.returnTrip) {
			// Create a copy of the outbound dates so we can sort them
			const outbounds = this.outbounds.slice();
			const inbounds = this.inbounds.slice();

			// Sort the outbound dates by price
			outbounds.sort((a, b) => a.price - b.price);
			inbounds.sort((a, b) => a.price - b.price);

			// Merge the outbound and inbound dates into one array of objects
			const cheapestDates = outbounds.map((outbound, index) => ({
				departDate: outbound.departDate,
				returnDate: inbounds[index].returnDate
			}));

			return cheapestDates;
		}

		const outbounds = this.outbounds.slice();
		outbounds.sort((a, b) => a.price - b.price);

		return outbounds.map(outbound => ({ departDate: outbound.departDate, departurePrice: outbound.price }));
	}
}
